import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import 'theme/markdownStyles.css';
import { useHistory, useLocation } from 'react-router';
import ReactMarkdown from 'react-markdown';
import { ReactComponent as PreviewIcon } from 'assets/icons/preview.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as TwoViewsIcon } from 'assets/icons/twoViews.svg';
import Paragraph from 'components/atoms/Paragraph';
import { database } from 'helpers/firebase';
import gfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Loading from 'components/atoms/Loading';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  height: calc(100vh - 64px);
  width: 100%;
`;

const Side = styled.div`
  height: calc(100vh - 64px);
  width: ${({ width }) => width}%;
  overflow-y: hidden;
  transition: width 0.3s ease-in-out;
`;

const Editor = styled.textarea`
  font-family: 'JetBrains Mono', monospace;
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.text};
  font-size: 16px;
  resize: none;
  outline: none;
  padding: 15px;
  height: 100%;
  width: 100%;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 8px;
    background-color: ${({ theme }) => theme.gray6};
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.gray5};
    border-radius: 20px;
  }
`;

const StyledReactMarkdown = styled(ReactMarkdown)`
  scroll-behavior: smooth;
  word-wrap: break-word;
  padding: 15px 30px;
  height: 100%;
  border-left: 2px solid ${({ theme }) => theme.gray4};
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 8px;
    background-color: ${({ theme }) => theme.gray6};
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.gray5};
    border-radius: 20px;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const Tooltip = styled.span`
  position: absolute;
  white-space: nowrap;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%) scale(0);
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.gray4};
  border-radius: 5px;
  padding: 2px 4px;
`;

const ButtonIcon = styled.button`
  position: relative;
  height: 48px;
  width: 48px;
  cursor: pointer;
  background-color: ${({ theme, active }) => (active ? theme.blue : 'transparent')};
  border: 2px solid ${({ theme, active }) => (active ? theme.blue : theme.gray4)};
  border-radius: 50%;
  margin-left: 10px;

  svg {
    fill: ${({ theme }) => theme.text};
    height: 32px;
    width: 32px;
  }

  :hover {
    background-color: ${({ theme, active }) => (active ? theme.blue : theme.gray4)};

    ${Tooltip} {
      transform: translateX(-50%) scale(1);
    }
  }
`;

const TitleInput = styled.input`
  height: 144px;
  width: 1000px;
  background-color: transparent;
  border: none;
  border-bottom: 4px solid ${({ theme }) => theme.gray4};
  margin: 0 15px;
  font-size: 64px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  outline: none;
  color: ${({ theme }) => theme.text};

  ::placeholder {
    color: ${({ theme }) => theme.textSecondary};
  }
`;

const Length = styled(Paragraph)`
  margin-top: 93px;
  color: ${({ theme, limit }) => (limit ? theme.red : theme.text)};
  font-size: 34px;
`;

const CreateBtn = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) ${({ scale }) => `scale(${1 + scale})`};
  height: 55px;
  width: 89px;
  border: none;
  border-radius: 15px;
  transform-origin: center center;
  transition: transform 0.15s ease-in-out;

  :not(:disabled) {
    cursor: pointer;
    background-color: ${({ theme }) => theme.blue};
  }
`;

const StyledTwoViewsIcon = styled(TwoViewsIcon)`
  transform: rotate(90deg);
`;

const components = {
  code({ inline, className, children }) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <SyntaxHighlighter style={atomDark} language={match[1]} PreTag="div">
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className="markdown__code">{children}</code>
    );
  },
  // h1({ children }) {
  //   return (
  //     <h1 id={children.toString().split(' ').join('-').split('(').join('').split(')').join('')}>
  //       <a
  //         className="anchor"
  //         aria-hidden="true"
  //         href={`#${children
  //           .toString()
  //           .split(' ')
  //           .join('-')
  //           .split('(')
  //           .join('')
  //           .split(')')
  //           .join('')}`}
  //       >
  //         <svg
  //           viewBox="0 0 16 16"
  //           version="1.1"
  //           width="16"
  //           height="16"
  //           aria-hidden="true"
  //           className="octicon"
  //         >
  //           <path
  //             fillRule="evenodd"
  //             d="M7.775 3.275a.75.75 0 001.06 1.06l1.25-1.25a2 2 0 112.83 2.83l-2.5 2.5a2 2 0 01-2.83 0 .75.75 0 00-1.06 1.06 3.5 3.5 0 004.95 0l2.5-2.5a3.5 3.5 0 00-4.95-4.95l-1.25 1.25zm-4.69 9.64a2 2 0 010-2.83l2.5-2.5a2 2 0 012.83 0 .75.75 0 001.06-1.06 3.5 3.5 0 00-4.95 0l-2.5 2.5a3.5 3.5 0 004.95 4.95l1.25-1.25a.75.75 0 00-1.06-1.06l-1.25 1.25a2 2 0 01-2.83 0z"
  //           />
  //         </svg>
  //       </a>
  //       {children}
  //     </h1>
  //   );
  // },
};

function Note({ noteId }) {
  const [highlightedSide, setHighlightedSide] = useState(0);
  const [value, setValue] = useState('');
  const [currentView, setCurrentView] = useState(1);
  const [widthPercentage, setWidthPercentage] = useState([60, 40]);
  const [isNewNote, setIsNewNote] = useState(false);
  const [title, setTitle] = useState('');
  const [titleLength, setTitleLength] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const waitForEl = setInterval(() => {
        try {
          const el = document.querySelector(location.hash);

          if (el) {
            clearInterval(waitForEl);
            el.scrollIntoView();
          }
        } catch (e) {
          clearInterval(waitForEl);
        }
      }, 150);
    }
  }, []);

  useEffect(() => {
    let widths = [];

    if (currentView === 0) {
      widths = [100, 0];
    } else if (currentView === 2) {
      widths = [0, 100];
    } else if (highlightedSide) {
      widths = [40, 60];
    } else widths = [60, 40];

    setWidthPercentage(widths);
  }, [currentView, highlightedSide]);

  useEffect(() => {
    setIsNewNote(noteId === 'nowa' || !noteId.length);
  }, [noteId]);

  useEffect(() => {
    if (!isNewNote && noteId) {
      const ref = database.ref('other/notes').child(noteId);

      ref.child('title').on('value', (data) => {
        if (data.exists()) {
          setTitle(data.val());

          ref.child('value').on('value', (data) => {
            setValue(data.val());
            setIsLoading(false);
          });
        } else {
          setIsLoading(false);
          setIsNewNote(true);
        }
      });
    } else {
      setIsLoading(false);
    }
  }, [isNewNote, noteId]);

  const handleChange = (e) => {
    const ref = database.ref('other/notes').child(noteId);
    ref.update({
      value: e.target.value,
    });
  };

  const handleTitleChange = (e) => {
    // eslint-disable-next-line no-shadow
    const { value } = e.target;

    if (value.length <= 30) {
      setTitle(value);
      setTitleLength(value.length);
    }
  };

  const handleCreateNote = () => {
    const ref = database.ref('other/notes');

    const key = ref.push();
    key
      .set({
        title: title.trim(),
        value: '',
        createdDate: new Date().getTime(),
      })
      .then(() => {
        history.push(`/notatki/${key.key}`);
      });
  };

  return (
    <Wrapper>
      {isLoading ? (
        <Loading />
      ) : isNewNote ? (
        <>
          <TitleInput
            type="text"
            placeholder="Tytuł notatki"
            value={title}
            onChange={handleTitleChange}
          />
          <Length limit={titleLength >= 30}>{titleLength}/30</Length>
          <CreateBtn
            type="button"
            scale={Math.PI * (titleLength / 30)}
            onClick={handleCreateNote}
            disabled={!titleLength}
          >
            <Paragraph>Stwórz</Paragraph>
          </CreateBtn>
        </>
      ) : (
        <>
          <Controls>
            <ButtonIcon type="button" active={currentView === 0} onClick={() => setCurrentView(0)}>
              <EditIcon />
              <Tooltip>Edytor</Tooltip>
            </ButtonIcon>
            <ButtonIcon type="button" active={currentView === 1} onClick={() => setCurrentView(1)}>
              <StyledTwoViewsIcon />
              <Tooltip>Edytor i podgląd</Tooltip>
            </ButtonIcon>
            <ButtonIcon type="button" active={currentView === 2} onClick={() => setCurrentView(2)}>
              <PreviewIcon />
              <Tooltip>Podgląd</Tooltip>
            </ButtonIcon>
          </Controls>
          <Side width={widthPercentage[0]} onClick={() => setHighlightedSide(0)}>
            <Editor onChange={handleChange} value={value} />
          </Side>
          <Side width={widthPercentage[1]} onClick={() => setHighlightedSide(1)}>
            <StyledReactMarkdown
              components={components}
              remarkPlugins={[gfm]}
              className="markdown"
              id="markdown"
            >
              {value}
            </StyledReactMarkdown>
          </Side>
        </>
      )}
    </Wrapper>
  );
}

Note.propTypes = {
  noteId: PropTypes.string,
};

Note.defaultProps = {
  noteId: '',
};

export default Note;
